import { Component } from "../../Component.js";
import { createEditor, createEditorDefsFromModel } from "../../utils/data-editor-helpers.js";

function resolveValue(value, context) {

    return typeof value === 'function' ? value.call(context) : value;

}

function width(value) {

    if (value === undefined || value === null)
        return '';
    if (value === 'auto' || value === 'max')
        return '.max';
    if (value === 'shrink' || value === 'min')
        return '.min';

    const numericValue = parseInt(value);
    if (Number.isNaN(numericValue) || numericValue <= 0)
        return '';

    return `.s${numericValue}`;

}

export class DataEditorControl extends Component {

    constructor(parent, options = {}) {

        options = Object.assign({
            data: null,
            dataModel: null,
            readOnly: false,
            canUserEditField: null,
            getFieldErrors: null,
            onFieldValueChange: null,
            editorDefs: null,
        }, options || {});

        super(parent, options);

        this.data = options.data;
        this.dataModel = options.dataModel;
        this.readOnly = options.readOnly;
        this.canUserEditField = options.canUserEditField;
        this.getFieldErrors = options.getFieldErrors;
        this.onFieldValueChange = options.onFieldValueChange;
        this.editorDefs = options.editorDefs;

        this.defaultEditorDefs = [];
        this.defaultEditorControls = [];

    };

    getData() {

        return resolveValue(this.data, this);

    };

    getDataModel() {

        return resolveValue(this.dataModel, this);

    };

    isReadOnly() {

        return resolveValue(this.readOnly, this) || false;

    };

    canEditField(fieldName) {

        if (typeof this.canUserEditField != 'function')
            return true;

        return this.canUserEditField.call(this, fieldName) !== false;

    };

    getErrorForField(fieldName) {

        if (typeof this.getFieldErrors != 'function')
            return null;

        return this.getFieldErrors.call(this, fieldName) || null;

    };

    getEditorDefs() {

        const editorDefs = resolveValue(this.editorDefs, this);

        if (Array.isArray(editorDefs))
            return editorDefs;

        const dataModel = this.getDataModel();

        if (!dataModel)
            return [];

        return createEditorDefsFromModel(dataModel);

    };

    getEditorControls() {

        return this.defaultEditorControls || [];

    };

    setEditorControls(editorControls) {

        this.defaultEditorControls = editorControls || [];
        return this.defaultEditorControls;

    };

    createDefaultEditorControls() {

        const dataModel = this.getDataModel();
        const editorDefs = this.getEditorDefs();

        this.defaultEditorDefs = editorDefs;

        return editorDefs
            .map((editorDef) => createEditor(Object.assign({}, editorDef, {
                readOnly: () => this.isReadOnly() || !this.canEditField(editorDef.fieldName),
            }), this, {
                data: () => this.getData(),
                dataModel,
                fields: () => dataModel?.getFields?.() || [],
                onFieldValueChange: (value, field, data) => {
                    if (typeof this.onFieldValueChange == 'function')
                        return this.onFieldValueChange.call(this, value, field, data);

                    if (!data || !field)
                        return false;

                    field.setValue(data, value);
                    data.$changed = true;
                    this.invalidate();
                    return true;
                }
            }))
            .filter((editor) => !!editor);

    };

    ensureEditorControls() {

        if (this.getEditorControls().length)
            return this.getEditorControls();

        const editorControls = this.createDefaultEditorControls();
        this.setEditorControls(editorControls);
        return editorControls;

    };

    validateData() {

        return this.getEditorControls().every((control) => {

            if (!control)
                return true;
            if (typeof control.validateData == 'function')
                return control.validateData();
            if (typeof control.validateValue == 'function')
                return control.validateValue();

            return true;

        });

    };

    async _prepare(options, activeRoute) {

        await super._prepare(options, activeRoute);
        this.ensureEditorControls();

    };

    async _refresh(options, activeRoute) {

        await super._refresh(options, activeRoute);
        this.ensureEditorControls();

    };

    renderContent(r, vm) {

        const renderer = r;
        this.ensureEditorControls();
        const editorControls = this.getEditorControls();

        if (!editorControls.length) {
            return renderer.el('div', 'muted', null, [
                'No editor fields available.'
            ]);
        }

        return renderer.grid({},
            editorControls.map((control, index) => this.renderEditorCell(renderer, vm, this.defaultEditorDefs[index], control))
        );

    };

    renderEditorCell(renderer, vm, editorDef, control) {

        const cellWidth = editorDef?.group?.width || editorDef?.width || control?.width || 12;
        const label = editorDef?.group?.label || editorDef?.label || '';
        const hideLabel = !!editorDef?.group?.hideLabel;
        const controlId = control?.getId ? control.getId() : control?.id;
        const fieldName = editorDef?.fieldName || null;
        const controlError = control?.getValueError ? control.getValueError() : null;
        const externalError = fieldName ? this.getErrorForField(fieldName) : null;

        return renderer.cell({ css: width(cellWidth) }, [
            renderer.field({
                label: hideLabel ? null : label,
                controlId: controlId || null,
                error: externalError || controlError,
            }, control ? control.render(renderer, vm) : null),
        ]);

    };

    _render(r, vm) {

        return this.renderContent(r, vm);

    };

}
